import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import {
  getReminderActivity,
  listReminders,
  mapLaravelError,
  previewReminders,
  runReminders,
  updateReminderSettings,
} from '../../services/platformRemindersApi';

// Domain value -> human label. Used everywhere domains are shown or edited.
const DOMAIN_LABELS = {
  hr_request: 'HR requests',
  vacation: 'Vacation',
  exit_entry: 'Exit / Entry',
};

const domainLabel = (value) => DOMAIN_LABELS[value] || value;

const FALLBACK_DOMAINS = ['hr_request', 'vacation', 'exit_entry'];

const statusColor = (status) => {
  const value = String(status || '').toLowerCase();
  if (value === 'active') return 'success';
  if (value === 'suspended') return 'warning';
  if (value === 'archived') return 'default';
  return 'default';
};

const clampInt = (value, min, fallback = 0) => {
  const num = Math.trunc(Number(value));
  if (!Number.isFinite(num)) return fallback;
  return Math.max(min, num);
};

const formatMax = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? String(num) : '∞';
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return String(iso);
  return date.toLocaleString();
};

// Build the PUT payload from a settings object, coercing/clamping numbers.
const toSettingsPayload = (settings) => ({
  enabled: Boolean(settings.enabled),
  threshold_days: clampInt(settings.threshold_days, 1, 1),
  cadence_days: clampInt(settings.cadence_days, 1, 1),
  max_reminders: clampInt(settings.max_reminders, 0, 0),
  domains: Array.isArray(settings.domains) ? settings.domains : [],
  skip_weekends: Boolean(settings.skip_weekends),
});

export default function ReminderAdminPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [global, setGlobal] = useState(null);
  const [rows, setRows] = useState([]);

  // Tracks which tenant id is mid-save from the inline Enabled switch.
  const [togglingId, setTogglingId] = useState(null);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [configState, setConfigState] = useState(null);
  const [previewState, setPreviewState] = useState(null);
  const [activityState, setActivityState] = useState(null);
  const [sendState, setSendState] = useState(null);

  const showToast = useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const availableDomains = useMemo(() => {
    const list = global?.available_domains;
    return Array.isArray(list) && list.length ? list : FALLBACK_DOMAINS;
  }, [global]);

  const loadReminders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await listReminders();
      setGlobal(payload?.global || null);
      setRows(Array.isArray(payload?.tenants) ? payload.tenants : []);
    } catch (requestError) {
      setError(mapLaravelError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReminders();
  }, [loadReminders]);

  const replaceRowSettings = useCallback((id, settings) => {
    setRows((previous) => previous.map((row) => (
      String(row.id) === String(id) ? { ...row, settings } : row
    )));
  }, []);

  // Inline Enabled switch: send the tenant's CURRENT settings with the flipped flag,
  // and revert + toast on error.
  const handleToggleEnabled = async (tenant, nextEnabled) => {
    setTogglingId(tenant.id);
    const previousSettings = tenant.settings || {};
    // Optimistic update.
    replaceRowSettings(tenant.id, { ...previousSettings, enabled: nextEnabled });

    try {
      const payload = toSettingsPayload({ ...previousSettings, enabled: nextEnabled });
      const response = await updateReminderSettings(tenant.id, payload);
      const settings = response?.settings || payload;
      replaceRowSettings(tenant.id, settings);
      showToast(
        `${tenant.display_name}: reminders ${settings.enabled ? 'enabled' : 'disabled'}.`,
        'success',
      );
    } catch (requestError) {
      replaceRowSettings(tenant.id, previousSettings);
      showToast(mapLaravelError(requestError).message, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // ---- Configure dialog -------------------------------------------------
  const openConfigure = (tenant) => {
    const settings = tenant.settings || {};
    setConfigState({
      tenant,
      saving: false,
      error: '',
      fieldErrors: {},
      form: {
        enabled: Boolean(settings.enabled),
        threshold_days: Number.isFinite(Number(settings.threshold_days)) ? Number(settings.threshold_days) : 3,
        cadence_days: Number.isFinite(Number(settings.cadence_days)) ? Number(settings.cadence_days) : 3,
        max_reminders: Number.isFinite(Number(settings.max_reminders)) ? Number(settings.max_reminders) : 5,
        domains: Array.isArray(settings.domains) ? settings.domains : [],
        skip_weekends: settings.skip_weekends !== false,
      },
    });
  };

  const closeConfigure = () => setConfigState(null);

  const updateConfigForm = (partial) => {
    setConfigState((prev) => (prev ? { ...prev, form: { ...prev.form, ...partial } } : prev));
  };

  const handleConfigSave = async () => {
    if (!configState) return;
    const { tenant, form } = configState;
    setConfigState((prev) => (prev ? { ...prev, saving: true, error: '', fieldErrors: {} } : prev));

    try {
      const payload = toSettingsPayload(form);
      const response = await updateReminderSettings(tenant.id, payload);
      const settings = response?.settings || payload;
      replaceRowSettings(tenant.id, settings);
      setConfigState(null);
      showToast(`${tenant.display_name}: reminder settings saved.`, 'success');
    } catch (requestError) {
      const mapped = mapLaravelError(requestError);
      setConfigState((prev) => (prev
        ? { ...prev, saving: false, error: mapped.message, fieldErrors: mapped.fieldErrors }
        : prev));
    }
  };

  // ---- Preview dialog ---------------------------------------------------
  const openPreview = async (tenant) => {
    setPreviewState({ tenant, loading: true, error: '', data: null });
    try {
      const data = await previewReminders(tenant.id);
      setPreviewState({ tenant, loading: false, error: '', data });
    } catch (requestError) {
      setPreviewState({ tenant, loading: false, error: mapLaravelError(requestError).message, data: null });
    }
  };

  const closePreview = () => setPreviewState(null);

  // ---- Activity dialog --------------------------------------------------
  const openActivity = async (tenant) => {
    setActivityState({ tenant, loading: true, error: '', data: null });
    try {
      const data = await getReminderActivity(tenant.id, { limit: 100 });
      setActivityState({ tenant, loading: false, error: '', data });
    } catch (requestError) {
      setActivityState({ tenant, loading: false, error: mapLaravelError(requestError).message, data: null });
    }
  };

  const closeActivity = () => setActivityState(null);

  // ---- Send now ---------------------------------------------------------
  const openSend = (tenant) => {
    setSendState({ tenant, running: false, result: null, error: '' });
  };

  const closeSend = () => {
    if (sendState?.running) return;
    setSendState(null);
  };

  const handleSendConfirm = async () => {
    if (!sendState) return;
    const { tenant } = sendState;
    setSendState((prev) => (prev ? { ...prev, running: true, error: '', result: null } : prev));
    try {
      const result = await runReminders(tenant.id);
      setSendState((prev) => (prev ? { ...prev, running: false, result } : prev));
      const sent = result?.sent;
      const failed = result?.failed;
      if (sent !== null && sent !== undefined) {
        showToast(`${tenant.display_name}: sent ${sent}, failed ${failed ?? 0}.`, 'success');
      } else {
        showToast(`${tenant.display_name}: send command completed.`, 'success');
      }
    } catch (requestError) {
      setSendState((prev) => (prev
        ? { ...prev, running: false, error: mapLaravelError(requestError).message }
        : prev));
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Request reminders
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Automated reminders to approvers about requests still awaiting their action.
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={loadReminders}
          >
            Refresh
          </Button>
        </Stack>

        {error ? (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={loadReminders}>
                Retry
              </Button>
            )}
          >
            {error}
          </Alert>
        ) : null}

        {global ? (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  size="small"
                  color={global.enabled ? 'success' : 'default'}
                  label={global.enabled ? 'Global switch: on' : 'Global switch: off'}
                />
                <Typography variant="body2" color="text.secondary">
                  Daily send time:&nbsp;
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {global.send_time || '-'}
                  </Box>
                  &nbsp;({global.timezone || '-'})
                </Typography>
              </Stack>

              {!global.enabled ? (
                <Alert severity="warning">
                  Reminders are globally disabled &mdash; the daily job will not run.
                </Alert>
              ) : null}

              <Typography variant="caption" color="text.secondary">
                Reminders are opt-in per tenant. Delivery depends on the platform scheduler
                running the daily reminder job.
              </Typography>
            </Stack>
          </Paper>
        ) : null}

        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Threshold</TableCell>
                  <TableCell>Cadence</TableCell>
                  <TableCell>Max</TableCell>
                  <TableCell>Domains</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        No tenants found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {rows.map((tenant) => {
                  const settings = tenant.settings || {};
                  const domains = Array.isArray(settings.domains) ? settings.domains : [];
                  const isToggling = String(togglingId) === String(tenant.id);

                  return (
                    <TableRow key={tenant.id} hover>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {tenant.display_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            key: {tenant.key}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColor(tenant.status)} label={tenant.status || 'unknown'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch
                            size="small"
                            checked={Boolean(settings.enabled)}
                            disabled={isToggling}
                            onChange={(event) => handleToggleEnabled(tenant, event.target.checked)}
                          />
                          {isToggling ? <CircularProgress size={16} /> : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{`${clampInt(settings.threshold_days, 1, 1)}d`}</TableCell>
                      <TableCell>{`every ${clampInt(settings.cadence_days, 1, 1)}d`}</TableCell>
                      <TableCell>{formatMax(settings.max_reminders)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {domains.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">None</Typography>
                          ) : domains.map((domain) => (
                            <Chip key={domain} size="small" variant="outlined" label={domainLabel(domain)} />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Configure">
                            <IconButton size="small" onClick={() => openConfigure(tenant)}>
                              <SettingsOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Preview">
                            <IconButton size="small" onClick={() => openPreview(tenant)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Activity">
                            <IconButton size="small" onClick={() => openActivity(tenant)}>
                              <HistoryOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send now">
                            <IconButton size="small" color="primary" onClick={() => openSend(tenant)}>
                              <SendOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>

      <ConfigureDialog
        state={configState}
        availableDomains={availableDomains}
        onClose={closeConfigure}
        onChange={updateConfigForm}
        onSave={handleConfigSave}
      />

      <PreviewDialog state={previewState} onClose={closePreview} />

      <ActivityDialog state={activityState} onClose={closeActivity} />

      <SendNowDialog state={sendState} onClose={closeSend} onConfirm={handleSendConfirm} />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const getFieldError = (fieldErrors, key) => fieldErrors?.[key] || '';

function ConfigureDialog({ state, availableDomains, onClose, onChange, onSave }) {
  if (!state) return null;
  const { tenant, form, saving, error, fieldErrors } = state;

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={saving ? undefined : onClose}>
      <DialogTitle>
        Configure reminders
        <Typography variant="body2" color="text.secondary">
          {tenant.display_name} ({tenant.key})
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <FormControlLabel
            control={(
              <Switch
                checked={form.enabled}
                onChange={(event) => onChange({ enabled: event.target.checked })}
              />
            )}
            label="Enabled"
          />

          <Divider />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Threshold (days)"
                value={form.threshold_days}
                onChange={(event) => onChange({ threshold_days: clampInt(event.target.value, 1, 1) })}
                inputProps={{ min: 1, step: 1 }}
                error={Boolean(getFieldError(fieldErrors, 'threshold_days'))}
                helperText={getFieldError(fieldErrors, 'threshold_days') || 'First reminder after this many days.'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Repeat every (days)"
                value={form.cadence_days}
                onChange={(event) => onChange({ cadence_days: clampInt(event.target.value, 1, 1) })}
                inputProps={{ min: 1, step: 1 }}
                error={Boolean(getFieldError(fieldErrors, 'cadence_days'))}
                helperText={getFieldError(fieldErrors, 'cadence_days') || 'Re-remind every N days.'}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Max reminders"
                value={form.max_reminders}
                onChange={(event) => onChange({ max_reminders: clampInt(event.target.value, 0, 0) })}
                inputProps={{ min: 0, step: 1 }}
                error={Boolean(getFieldError(fieldErrors, 'max_reminders'))}
                helperText={getFieldError(fieldErrors, 'max_reminders') || '0 = unlimited'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Domains"
                value={form.domains}
                onChange={(event) => {
                  const selected = Array.isArray(event.target.value) ? event.target.value : [];
                  onChange({ domains: selected });
                }}
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    selected && selected.length
                      ? selected.map((value) => domainLabel(value)).join(', ')
                      : 'No request types selected'
                  ),
                }}
                error={Boolean(getFieldError(fieldErrors, 'domains'))}
                helperText={getFieldError(fieldErrors, 'domains') || 'Which request types send reminders.'}
              >
                {availableDomains.map((value) => (
                  <MenuItem key={value} value={value}>
                    <Checkbox checked={form.domains.includes(value)} />
                    <ListItemText primary={domainLabel(value)} />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.skip_weekends}
                    onChange={(event) => onChange({ skip_weekends: event.target.checked })}
                  />
                )}
                label="Skip weekends"
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ItemStatusChip({ item }) {
  if (item.would_send) {
    return <Chip size="small" color="success" label="will send" />;
  }
  return <Chip size="small" variant="outlined" label={item.reason || 'will not send'} />;
}

function ReferenceLink({ item }) {
  const text = item.reference || item.subject || '-';
  if (item.deep_link) {
    return (
      <Link href={item.deep_link} target="_blank" rel="noopener noreferrer" underline="hover">
        {text}
      </Link>
    );
  }
  return <span>{text}</span>;
}

function PreviewDialog({ state, onClose }) {
  if (!state) return null;
  const { tenant, loading, error, data } = state;
  const totals = data?.totals || {};
  const recipients = Array.isArray(data?.recipients) ? data.recipients : [];

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        Preview reminders
        <Typography variant="body2" color="text.secondary">
          {tenant.display_name} ({tenant.key})
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && !error ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {`${totals.recipients ?? 0} approver(s), ${totals.items ?? 0} item(s) past threshold, ${totals.would_send ?? 0} would be emailed now.`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                No emails are sent by preview.
              </Typography>
            </Box>

            {recipients.length === 0 ? (
              <Alert severity="info">
                No requests are past the threshold for this tenant right now.
              </Alert>
            ) : null}

            {recipients.map((recipient) => {
              const items = Array.isArray(recipient.items) ? recipient.items : [];
              return (
                <Paper key={recipient.employee_code || recipient.email} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {recipient.name} ({recipient.employee_code})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {recipient.email}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Chip size="small" variant="outlined" label={`${recipient.due_count ?? 0} due`} />
                        <Chip size="small" color="success" label={`${recipient.send_count ?? 0} will send`} />
                      </Stack>
                    </Stack>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell>Subject</TableCell>
                            <TableCell>Waiting</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item) => (
                            <TableRow key={item.item_key || `${item.domain}-${item.id}`}>
                              <TableCell>{item.type_label}</TableCell>
                              <TableCell><ReferenceLink item={item} /></TableCell>
                              <TableCell>{item.subject}</TableCell>
                              <TableCell>{item.waiting_label}</TableCell>
                              <TableCell><ItemStatusChip item={item} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function ActivityRow({ entry }) {
  const [open, setOpen] = useState(false);
  const items = Array.isArray(entry.items) ? entry.items : [];

  return (
    <>
      <TableRow hover>
        <TableCell>{formatDateTime(entry.created_at)}</TableCell>
        <TableCell>
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {entry.name || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {entry.employee_code}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>{entry.count ?? items.length}</TableCell>
        <TableCell align="right">
          <IconButton size="small" onClick={() => setOpen((prev) => !prev)} disabled={items.length === 0}>
            {open ? <KeyboardArrowUpOutlinedIcon fontSize="small" /> : <KeyboardArrowDownOutlinedIcon fontSize="small" />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5, pl: 2 }}>
              <Stack spacing={0.75}>
                {items.map((item, index) => (
                  <Stack
                    key={item.item_key || `${entry.id}-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip size="small" variant="outlined" label={item.type_label} />
                    <Typography variant="body2"><ReferenceLink item={item} /></Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.waiting_label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function ActivityDialog({ state, onClose }) {
  if (!state) return null;
  const { tenant, loading, error, data } = state;
  const activity = Array.isArray(data?.activity) ? data.activity : [];

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>
        Reminder activity
        <Typography variant="body2" color="text.secondary">
          {tenant.display_name} ({tenant.key})
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && error ? <Alert severity="error">{error}</Alert> : null}

        {!loading && !error && activity.length === 0 ? (
          <Alert severity="info">
            No reminders have been sent for this tenant yet.
          </Alert>
        ) : null}

        {!loading && !error && activity.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activity.map((entry) => (
                  <ActivityRow key={entry.id} entry={entry} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function SendNowDialog({ state, onClose, onConfirm }) {
  if (!state) return null;
  const { tenant, running, result, error } = state;
  const done = Boolean(result) || Boolean(error);

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Send reminders now</DialogTitle>
      <DialogContent dividers>
        {!done ? (
          <DialogContentText>
            This emails real approvers in <strong>{tenant.display_name}</strong> immediately.
            Threshold and cadence still apply, but the enabled flag and weekend-skip are bypassed.
            Continue?
          </DialogContentText>
        ) : null}

        {running ? (
          <Box sx={{ pt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Sending reminders... this can take a few seconds.
            </Typography>
          </Box>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}

        {result ? (
          <Stack spacing={1}>
            <Alert severity={result.ok ? 'success' : 'warning'}>
              {result.sent !== null && result.sent !== undefined
                ? `Sent ${result.sent}, failed ${result.failed ?? 0}.`
                : 'Command completed.'}
            </Alert>
            {result.output ? (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 220,
                  overflow: 'auto',
                }}
              >
                {result.output}
              </Box>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        {!done ? (
          <>
            <Button onClick={onClose} disabled={running}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendOutlinedIcon />}
              onClick={onConfirm}
              disabled={running}
            >
              {running ? 'Sending...' : 'Send now'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
